import { Collapse } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import Badge from '@material-ui/core/Badge';
import clsx from 'clsx';
import React from 'react';
import { NavLink } from 'react-router-dom';
import { formatToSlugCase } from '../../../../utils/helpers';
import Typography from '../../../common/Typography';
import * as MENU_ICONS from '../../../svg/Icons/Menu';
import { IconDashboard } from '../../../svg/Icons/Menu';

export const getGroupTourState = (group, authUser, activeGroupOnboardingTour, todoCounts) => {
  const tourActive = Boolean((group.active && authUser?.onViewTour) || activeGroupOnboardingTour(group.key));
  const isWorkplaceGroup = group.key === 'Dashboard';
  const isWorkplaceOpen = isWorkplaceGroup && tourActive;
  const shouldShowTodoOnWorkplaceGroup = isWorkplaceGroup && todoCounts.notCompleted > 0 && !isWorkplaceOpen;

  return {
    tourActive,
    isWorkplaceOpen,
    shouldShowTodoOnWorkplaceGroup,
    tourStyle: tourActive
      ? {
          position: 'relative',
          zIndex: 1703,
          background: 'white',
        }
      : null,
  };
};

export const getGroupTourLayoutStyles = (tourStyle, openDrawer) => {
  if (!tourStyle) {
    return { headerStyle: undefined, contentStyle: undefined };
  }

  if (!openDrawer) {
    return {
      headerStyle: { ...tourStyle, paddingTop: 0 },
      contentStyle: { ...tourStyle, paddingBottom: 0, paddingRight: 0 },
    };
  }

  return {
    headerStyle: { ...tourStyle, paddingTop: '16px' },
    contentStyle: { ...tourStyle, paddingBottom: '16px', paddingRight: '40px' },
  };
};

export const renderAppMenuRouteItem = ({
  groupKey,
  groupIndex,
  route,
  showMenuText,
  isWorkplaceOpen,
  pendingLMS,
  classes,
  clickOnLink,
  getRoutePath,
  getNavIsActive,
  renderRouteContent,
}) => {
  const IconComponent = MENU_ICONS[route.icon] || IconDashboard;
  const linkPath = getRoutePath(groupKey, route);
  const showLmsSpinner = Boolean(route.function && pendingLMS);
  const cursorStyle = route.disabled ? 'not-allowed' : 'pointer';
  const pointerEvents = route.disabled ? 'none' : 'auto';
  const activeClassName = route.path === 'none' ? '' : classes.active;

  return (
    <Grid
      item
      key={route.key}
      className={`${formatToSlugCase(groupKey)}-${formatToSlugCase(route.key)}`}
      style={{ cursor: cursorStyle }}
    >
      <Link
        onClick={(event) => clickOnLink(event, route, groupIndex, groupKey)}
        to={linkPath}
        isActive={getNavIsActive(route)}
        component={NavLink}
        className={classes.inactive}
        activeClassName={activeClassName}
        underline="none"
        disabled
        style={{
          pointerEvents,
        }}
      >
        <IconComponent />
        <Typography
          component="span"
          fontFamily="lexend"
          variant="subtitle1"
          className={classes.linkText}
          style={{ position: 'relative' }}
        >
          {showMenuText ? renderRouteContent(route, isWorkplaceOpen) : null}

          {showLmsSpinner ? (
            <span
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                right: '-25px',
                left: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end',
                visibility: 'visible',
              }}
            >
              <CircularProgress style={{ marginLeft: '10px' }} color="inherit" size="1.29em" />
            </span>
          ) : null}
        </Typography>
      </Link>
    </Grid>
  );
};

export const renderAppMenuGroup = ({
  group,
  groupIndex,
  submenuItems,
  authUser,
  currentOnboardingStep,
  openDrawer,
  showMenuText,
  isOnDashboard,
  todoCounts,
  pendingLMS,
  classes,
  badgeClasses,
  safeTrans,
  activeGroupOnboardingTour,
  setActive,
  clickOnLink,
  getRoutePath,
  getNavIsActive,
  renderRouteContent,
}) => {
  if (!authUser) return null;

  const { tourActive, isWorkplaceOpen, shouldShowTodoOnWorkplaceGroup, tourStyle } = getGroupTourState(
    group,
    authUser,
    activeGroupOnboardingTour,
    todoCounts
  );
  const GroupIcon = MENU_ICONS[group.icon] || IconDashboard;
  const onboardingOverlay = Boolean(!authUser.onViewTour && currentOnboardingStep > 0);
  const visibleRoutes = submenuItems.filter((item) => item.showMenu);
  const { headerStyle, contentStyle } = getGroupTourLayoutStyles(tourStyle, openDrawer);
  const onGroupClick = group.disabled ? null : () => setActive(groupIndex);

  return (
    <Grid container item key={group.key} className={formatToSlugCase(group.key)} direction={'column'}>
      {onboardingOverlay ? (
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1704,
          }}
        />
      ) : null}

      <Grid item>
        <Grid
          container
          className={clsx(classes.groupInactive, {
            [classes.groupActive]: group.active,
            [classes.groupDisabled]: group.disabled,
          })}
          alignItems={'center'}
          wrap="nowrap"
          onClick={onGroupClick}
          style={headerStyle}
        >
          <Grid item>
            <div
              className={clsx(classes.groupIconInactive, {
                [classes.groupIconActive]: tourActive,
                [classes.groupDisabled]: group.disabled,
              })}
            >
              <GroupIcon />
            </div>
          </Grid>

          <Grid item>
            <Typography
              component="span"
              fontFamily="lexend"
              variant="body2"
              className={clsx(classes.groupTitle, {
                [classes.groupTitleActive]: tourActive,
                [classes.groupDisabled]: group.disabled,
              })}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', columnGap: 8 }}>
                {showMenuText ? <span>{safeTrans(group.key)}</span> : null}

                {openDrawer && shouldShowTodoOnWorkplaceGroup ? (
                  <Badge
                    badgeContent={todoCounts.notCompleted}
                    color="secondary"
                    overlap="rectangular"
                    classes={{
                      root: badgeClasses.badgeRootInline,
                      badge: clsx(badgeClasses.badgeInline, !isOnDashboard && badgeClasses.badgePulse),
                    }}
                  />
                ) : null}
              </span>
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid item style={contentStyle}>
        <Collapse in={tourActive} appear={true} unmountOnExit={false} mountOnEnter={true}>
          <Grid container direction={'column'} spacing={3} className={classes.groupContent}>
            <Grid item />
            {visibleRoutes.map((route) =>
              renderAppMenuRouteItem({
                groupKey: group.key,
                groupIndex,
                route,
                showMenuText,
                isWorkplaceOpen,
                pendingLMS,
                classes,
                clickOnLink,
                getRoutePath,
                getNavIsActive,
                renderRouteContent,
              })
            )}
          </Grid>
        </Collapse>
      </Grid>
    </Grid>
  );
};
